import { useCallback, useEffect, useRef, useState } from "react";

type ElementSize = { width: number; height: number };

/** Tracks an element's content-box size; stays `{ 0, 0 }` where `ResizeObserver` does not exist (jsdom). */
export const useElementSize = <Element extends HTMLElement>() => {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((element: Element | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!element || typeof ResizeObserver === "undefined") {
      return;
    }

    observerRef.current = new ResizeObserver(([entry]) => {
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observerRef.current.observe(element);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { ref, ...size };
};
